import { ILeaderboardItem, ILeaderboardOngoingItem } from 'src/types'

import {
  Paper,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Avatar,
  Box,
  useTheme,
  Tooltip,
  Link,
} from '@mui/material'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { getRaPathWithChallangeFilter } from 'src/common/utils'
import { CountdownTimer } from '../countdown-timer'
import { useMe } from 'src/hooks'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'

interface IProps {
  item: ILeaderboardItem | ILeaderboardOngoingItem
  isOverall?: boolean
}

export const LeaderboardTable = ({ item, isOverall }: IProps) => {
  const { data: user } = useMe()
  const theme = useTheme()

  if (item?.count < 1) {
    return null
  }

  const extra =
    item?.userPlacement && item.userPlacement.position > item.leaderboard.length
      ? item.userPlacement
      : null

  const truncate = (addr: string) => `${addr.slice(0, 6)}…${addr.slice(-4)}`

  return (
    <Paper
      sx={{
        p: 2,
        height: '100%',
        border: isOverall ? `1px solid ${theme.palette.primary.main}` : 'none',
      }}
      elevation={isOverall ? 4 : 2}
    >
      {isOverall && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '8px' }}>
          <Typography variant="h6">{'Overall'}</Typography>
          <EmojiEventsIcon
            sx={{ color: theme.palette.custom.primary, width: '28px', height: '28px' }}
          />
        </Box>
      )}

      {'name' in item && (
        <Typography variant="h6" sx={{ mb: '8px' }}>
          <Link href={getRaPathWithChallangeFilter(item.name)}>{item.name}</Link>
        </Typography>
      )}
      {'description' in item && (
        <Typography variant="body2" sx={{ color: theme.palette.custom.grey2, mb: '8px' }}>
          {item.description}
        </Typography>
      )}

      {'name' in item && (
        <Box sx={{ mb: 2, textAlign: 'center' }}>
          <CountdownTimer endDate={item.endDate} timeZone={user?.timezone} />
        </Box>
      )}

      {'name' in item && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '8px',
            mb: '10px',
          }}
        >
          {item?.traiPool && (
            <MetricCard label="TRAI Pool" value={Number(item.traiPool).toLocaleString()} />
          )}

          {item?.totalPoints && (
            <MetricCard label="Total pts" value={Number(item.totalPoints).toLocaleString()} />
          )}

          <MetricCard label="Scanners" value={item.count} />
        </Box>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell sx={{ px: '6px' }}>Username</TableCell>
              <TableCell align="right" sx={{ pl: '6px', pr: '12px' }}>
                Points
              </TableCell>
              <TableCell align="right" sx={{ px: '6px' }}>
                TRAI
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {item.leaderboard.map((p, i) => {
              const isYou = item.userPlacement?.wallet_address === p.wallet_address
              return (
                <TableRow key={p.wallet_address} selected={isYou}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell sx={{ px: '6px' }}>
                    <Box display="flex" alignItems="center" columnGap={1}>
                      {p.image ? (
                        <Avatar src={p.image} sx={{ width: 28, height: 28 }} />
                      ) : (
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: theme.palette.custom.darkBlue2,
                          }}
                        >
                          <AccountCircleIcon sx={{ width: 28, height: 28 }} />
                        </Avatar>
                      )}
                      <Tooltip title={p.wallet_address}>
                        <Typography variant="body2">
                          {p.name ?? truncate(p.wallet_address)}
                        </Typography>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ pl: '6px', pr: '12px' }}>
                    {isYou ? (
                      <Link href={getRaPathWithChallangeFilter('name' in item ? item.name : null)}>
                        {p.points}
                      </Link>
                    ) : (
                      <Typography fontSize={'14px'}>{p.points}</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ px: '6px' }}>
                    {p.trai.toFixed(2)}
                  </TableCell>
                </TableRow>
              )
            })}

            {extra && extra.position > item.leaderboard.length + 1 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  …
                </TableCell>
              </TableRow>
            )}

            {extra && (
              <TableRow sx={{ backgroundColor: theme.palette.action.hover }} selected>
                <TableCell>{extra.position}</TableCell>
                <TableCell sx={{ px: '6px' }}>
                  <Box display="flex" alignItems="center" columnGap={1}>
                    {extra.image ? (
                      <Avatar src={extra.image} sx={{ width: 28, height: 28 }} />
                    ) : (
                      <Avatar
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: theme.palette.custom.darkBlue2,
                        }}
                      >
                        <AccountCircleIcon sx={{ width: 28, height: 28 }} />
                      </Avatar>
                    )}
                    <Tooltip title={extra.wallet_address}>
                      <Typography variant="body2">
                        {extra.name ?? truncate(extra.wallet_address)}
                      </Typography>
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell sx={{ px: '6px' }} align="right">
                  <Link href={getRaPathWithChallangeFilter('name' in extra ? extra.name : null)}>
                    {extra.points}
                  </Link>
                </TableCell>
                <TableCell align="right">{extra.trai}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* {item.userPlacement && (
        <Box mt={1} p={1} bgcolor="action.selected" borderRadius={1}>
          <Typography variant="body2">
            Your rank: #{item.userPlacement.position} — {item.userPlacement.points} pts,{' '}
            {item.userPlacement.trai.toFixed(2)} TRAI
          </Typography>
        </Box>
      )} */}
    </Paper>
  )
}

interface MetricCardProps {
  label: string
  value: string | number
}

const MetricCard = ({ label, value }: MetricCardProps) => (
  <Paper
    variant="outlined"
    sx={{
      p: '8px',
      textAlign: 'center',
      flex: 1,
      minWidth: '90px',
    }}
  >
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
    <Typography fontSize={'18px'} fontWeight={600} sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Paper>
)
